import {
  HttpServiceInterface,
  HttpServiceInternalRequestConfig,
  HttpServiceRequestConfig,
  HttpServiceRequestParams,
  HttpServiceResponse,
} from './types';
import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { clearAuthData } from '@/domains/user/utils/clearAuthData';
import { HttpActionCodes } from '@/enums/HttpActionCodes';
import { HttpMethods } from '@/enums/HttpMethods';
import { RootPaths } from '@/routes/paths';
import SnackService from '@/services/SnackService/SnackService';
import TokenService from '@/services/TokenService/TokenService';
import {
  EXCLUDED_URLS,
  HTTP_SERVICE_CACHE_REQUESTS,
  HTTP_SERVICE_DEDUPLICATION_TTL,
  HTTP_SERVICE_UNAUTHORIZED_REQUEST_EXCEPTIONS,
} from './constants';

const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

axiosInstance.interceptors.request.use((config: HttpServiceInternalRequestConfig) => {
  const authToken = TokenService.getTokenData();

  const isExcluded = EXCLUDED_URLS.some((url) => config.url?.startsWith(url));

  if (authToken && !isExcluded) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }

  const requestHeaders = config.headers;
  const headerNameMatcher = config?.skipHeaderMatcher;

  for (const key in requestHeaders) {
    if (
      headerNameMatcher &&
      headerNameMatcher.test(key.toLowerCase()) &&
      Object.prototype.hasOwnProperty.call(requestHeaders, key)
    ) {
      delete config.headers[key];
    }
  }
  config.headers = requestHeaders;

  return config;
});

axiosInstance.interceptors.response.use(
  (config) => config,
  (config) => {
    const { status, data } = config?.response || {};
    const url = config?.config?.url || '';

    if (status === 401) {
      const isUnauthorizedException = HTTP_SERVICE_UNAUTHORIZED_REQUEST_EXCEPTIONS.some((exception) =>
        url.includes(exception),
      );
      if (!isUnauthorizedException) {
        clearAuthData();
        location.href = RootPaths.SIGN_IN;
      }
    }

    if (status !== 200 && data && typeof data === 'object' && 'name' in data && 'message' in data) {
      if (HttpActionCodes.RedirectToResetPassword === data.actionCode) {
        location.href = `${RootPaths.FORGOT_PASSWORD}?${HttpActionCodes.RedirectToResetPassword}=true`;
        return;
      }

      SnackService.add({
        type: SnackType.Error,
        title: data.name,
        content: data.message,
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
    }

    return Promise.reject(config);
  },
);

class HttpServiceClass implements HttpServiceInterface {
  public get<T>(
    path: string,
    params: HttpServiceRequestParams = {},
    config: HttpServiceRequestConfig = {},
  ): Promise<HttpServiceResponse<T>> {
    const { key, existing, now, shouldReturnExisting } = HttpServiceClass.checkExistingInCache(
      HttpMethods.GET,
      path,
      params,
      config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
    );

    if (existing && shouldReturnExisting) return existing.promise;

    const requestPromise = axiosInstance
      .get<T>(path, {
        ...config,
        params,
      })
      .finally(() => {
        setTimeout(
          () => HttpServiceClass.deleteCache(key),
          config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
        );
      });

    HttpServiceClass.setCache(key, now, requestPromise);

    return requestPromise;
  }

  public post<T>(
    path: string,
    data?: Record<string, any> | number | string,
    config: HttpServiceRequestConfig = {},
  ): Promise<HttpServiceResponse<T>> {
    const { key, existing, now, shouldReturnExisting } = HttpServiceClass.checkExistingInCache(
      HttpMethods.POST,
      path,
      {
        postData: data,
        postParams: config.params,
      },
      config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
    );

    if (existing && shouldReturnExisting) return existing.promise;

    const requestPromise = axiosInstance.post<T>(path, data, config).finally(() => {
      setTimeout(
        () => HttpServiceClass.deleteCache(key),
        config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
      );
    });

    HttpServiceClass.setCache(key, now, requestPromise);

    return requestPromise;
  }

  public put<T>(
    path: string,
    data: Record<string, any>,
    config: HttpServiceRequestConfig = {},
  ): Promise<HttpServiceResponse<T>> {
    const { key, existing, now, shouldReturnExisting } = HttpServiceClass.checkExistingInCache(
      HttpMethods.PUT,
      path,
      data,
      config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
    );

    if (existing && shouldReturnExisting) return existing.promise;

    const requestPromise = axiosInstance.put<T>(path, data, config).finally(() => {
      setTimeout(
        () => HttpServiceClass.deleteCache(key),
        config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
      );
    });

    HttpServiceClass.setCache(key, now, requestPromise);

    return requestPromise;
  }

  public patch<T>(
    path: string,
    data: Record<string, any>,
    config: HttpServiceRequestConfig = {},
  ): Promise<HttpServiceResponse<T>> {
    const { key, existing, now, shouldReturnExisting } = HttpServiceClass.checkExistingInCache(
      HttpMethods.PATCH,
      path,
      data,
      config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
    );

    if (existing && shouldReturnExisting) return existing.promise;

    const requestPromise = axiosInstance
      .patch<T>(path, data, {
        ...config,
        headers: {
          ...axios.defaults.headers.common,
          ...config.headers,
          accept: '*/*',
        },
      })
      .finally(() => {
        setTimeout(
          () => HttpServiceClass.deleteCache(key),
          config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
        );
      });

    HttpServiceClass.setCache(key, now, requestPromise);

    return requestPromise;
  }

  public delete<T>(path: string, config: HttpServiceRequestConfig = {}): Promise<HttpServiceResponse<T>> {
    const { key, existing, now, shouldReturnExisting } = HttpServiceClass.checkExistingInCache(
      HttpMethods.DELETE,
      path,
      undefined,
      config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
    );

    if (existing && shouldReturnExisting) return existing.promise;

    const requestPromise = axiosInstance.delete<T>(path, config).finally(() => {
      setTimeout(
        () => HttpServiceClass.deleteCache(key),
        config.httpServiceDeduplicationTTL ?? HTTP_SERVICE_DEDUPLICATION_TTL,
      );
    });

    HttpServiceClass.setCache(key, now, requestPromise);

    return requestPromise;
  }

  public setHeader(header: string, value: string): void {
    axiosInstance.defaults.headers.common[header] = value;
  }

  public removeHeader(header: string): void {
    delete axiosInstance.defaults.headers.common[header];
  }

  public static generateRequestKey(config: { method: string; url: string; params?: any; data?: any }) {
    const { method, url, params, data } = config;
    return JSON.stringify({ method, url, params, data });
  }

  public static checkExistingInCache(
    method: HttpMethods,
    url: string,
    data?: any,
    httpServiceDeduplicationTTL: number = HTTP_SERVICE_DEDUPLICATION_TTL,
  ): {
    key: string;
    existing: { timestamp: number; promise: Promise<any> } | undefined;
    now: number;
    shouldReturnExisting: boolean;
  } {
    const key = HttpServiceClass.generateRequestKey({ method, url, data });
    const now = Date.now();
    const existing = HTTP_SERVICE_CACHE_REQUESTS.get(key);

    return {
      key,
      existing,
      now,
      shouldReturnExisting: Boolean(existing && now - existing.timestamp < httpServiceDeduplicationTTL),
    };
  }

  public static setCache(key: string, timestamp: number, promise: Promise<any>) {
    HTTP_SERVICE_CACHE_REQUESTS.set(key, { timestamp, promise });
  }

  public static deleteCache(key: string) {
    HTTP_SERVICE_CACHE_REQUESTS.delete(key);
  }
}

const HttpService = new HttpServiceClass();

export default HttpService;
